// src/components/MinesweeperGame.js - 支持手机长按标旗
import React, { useState, useEffect, useRef } from 'react';

const MinesweeperGame = ({ room_id, rows, cols, initialBoard, initialMyRevealed, initialOppRevealed, initialMyFlags, socket, userId, opponentId }) => {
  const [board] = useState(() => JSON.parse(initialBoard));
  const [myRevealed, setMyRevealed] = useState(() => initialMyRevealed || Array(rows).fill().map(() => Array(cols).fill(false)));
  const [opponentRevealed, setOpponentRevealed] = useState(() => initialOppRevealed || Array(rows).fill().map(() => Array(cols).fill(false)));
  const [myFlags, setMyFlags] = useState(() => initialMyFlags || Array(rows).fill().map(() => Array(cols).fill(false)));
  const [gameEnded, setGameEnded] = useState(false);
  const [winner, setWinner] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [showResultOverlay, setShowResultOverlay] = useState(false);
  const [mineHitPos, setMineHitPos] = useState(null);
  const touchTimerRef = useRef(null);
  const touchStartPosRef = useRef(null);

  const totalMines = board.flat().filter(cell => cell === -1).length;
  const flaggedCount = myFlags.flat().filter(flag => flag).length;
  const revealedSafeCount = myRevealed.flat().filter((revealed, idx) => revealed && board.flat()[idx] !== -1).length;
  const totalSafeCells = rows * cols - totalMines;
  const progress = totalSafeCells > 0 ? Math.round((revealedSafeCount / totalSafeCells) * 100) : 0;

  // 游戏结束后自动显示所有雷
  useEffect(() => {
    if (gameEnded) {
      let newMyRevealed = myRevealed.map(row => [...row]);
      let newOppRevealed = opponentRevealed.map(row => [...row]);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (board[r][c] === -1) {
            newMyRevealed[r][c] = true;
            newOppRevealed[r][c] = true;
          }
        }
      }
      setMyRevealed(newMyRevealed);
      setOpponentRevealed(newOppRevealed);
    }
  }, [gameEnded, board, rows, cols]);

  useEffect(() => {
    if (!socket) return;

    const handleStateUpdate = (data) => {
      if (data.myRevealed) setMyRevealed(data.myRevealed);
      if (data.oppRevealed) setOpponentRevealed(data.oppRevealed);
      if (data.myFlags) setMyFlags(data.myFlags);
    };

    const handleGameResult = (data) => {
      setGameEnded(true);
      setWinner(data.winner_id);
      setGameResult(data);
      setShowResultOverlay(true);
      if (data.mineRow !== undefined && data.mineCol !== undefined) {
        setMineHitPos({ row: data.mineRow, col: data.mineCol });
      }
    };

    socket.on('minesweeper_state_update', handleStateUpdate);
    socket.on('minesweeper_game_result', handleGameResult);

    return () => {
      socket.off('minesweeper_state_update', handleStateUpdate);
      socket.off('minesweeper_game_result', handleGameResult);
    };
  }, [socket, userId]);

  const handleCellClick = (row, col) => {
    if (gameEnded) return;
    if (myRevealed[row][col]) return;
    socket.emit('minesweeper_reveal', { room_id, row, col, token: localStorage.getItem('memo_token') });
  };

  const handleFlag = (row, col) => {
    if (gameEnded) return;
    if (myRevealed[row][col]) return;
    const newFlags = [...myFlags.map(r => [...r])];
    newFlags[row][col] = !newFlags[row][col];
    setMyFlags(newFlags);
    socket.emit('minesweeper_flag', { room_id, row, col, token: localStorage.getItem('memo_token') });
  };

  // 触摸事件：区分点击和长按
  const handleTouchStart = (e, row, col) => {
    e.preventDefault();
    touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    touchTimerRef.current = setTimeout(() => {
      // 长按触发标旗
      handleFlag(row, col);
      touchTimerRef.current = null;
    }, 500);
  };

  const handleTouchEnd = (e, row, col) => {
    e.preventDefault();
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
      // 短按触发翻开
      handleCellClick(row, col);
    }
    touchStartPosRef.current = null;
  };

  const handleTouchCancel = (e) => {
    if (touchTimerRef.current) {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = null;
    }
  };

  const getCellContent = (value, revealed, flag) => {
    if (!revealed) return flag ? '🚩' : '?';
    if (value === -1) return '💣';
    return value === 0 ? '' : value;
  };

  const renderCell = (value, revealed, row, col, isMineBoard = true, flag = false) => {
    const isRevealed = revealed;
    const isMineHit = mineHitPos && mineHitPos.row === row && mineHitPos.col === col && value === -1;
    const hitStyle = isMineHit ? { boxShadow: '0 0 0 3px #f39c12', zIndex: 2, transform: 'scale(1.02)' } : {};
    const bgColor = isRevealed ? (value === -1 ? '#e74c3c' : '#ecf0f1') : '#2c3e50';
    const textColor = isRevealed ? (value === -1 ? 'white' : '#333') : '#ecf0f1';
    const cursor = isMineBoard && !gameEnded && !isRevealed ? 'pointer' : 'default';
    const numberColor = ['#3498db', '#2ecc71', '#f1c40f', '#e67e22', '#e74c3c', '#9b59b6', '#1abc9c', '#95a5a6'];

    return (
      <div
        key={`${row}-${col}`}
        onClick={() => isMineBoard && !gameEnded && !isRevealed && handleCellClick(row, col)}
        onContextMenu={(e) => {
          e.preventDefault();
          if (isMineBoard && !gameEnded && !isRevealed) handleFlag(row, col);
        }}
        onTouchStart={(e) => isMineBoard && !gameEnded && !isRevealed && handleTouchStart(e, row, col)}
        onTouchEnd={(e) => isMineBoard && !gameEnded && !isRevealed && handleTouchEnd(e, row, col)}
        onTouchCancel={handleTouchCancel}
        style={{
          width: '40px',
          height: '40px',
          backgroundColor: bgColor,
          border: '1px solid #bdc3c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          cursor: cursor,
          color: isRevealed && value > 0 ? numberColor[value] : textColor,
          userSelect: 'none',
          transition: 'background 0.1s ease',
          ...hitStyle,
          touchAction: 'none' // 防止触摸时页面滚动
        }}
      >
        {getCellContent(value, isRevealed, flag)}
      </div>
    );
  };

  const handleBackToCenter = () => {
    window.location.href = '/game-center';
  };

  const handleCloseOverlay = () => {
    setShowResultOverlay(false);
  };

  const renderGameOverlay = () => {
    if (!showResultOverlay) return null;
    const isWinner = winner === userId;
    let title = '';
    let message = '';
    
    if (isWinner) {
        if (gameResult?.reason === 'mine') {
        title = '🎉 胜利！';
        message = '对手踩中了地雷，你获胜！';
        } else {
        title = '🎉 胜利！';
        message = '你成功扫清了所有地雷！';
        }
    } else {
        if (gameResult?.reason === 'mine') {
        title = '💀 失败';
        message = `你在 (${(gameResult.mineRow ?? 0) + 1}, ${(gameResult.mineCol ?? 0) + 1}) 踩中了地雷`;
        } else {
        title = '💀 失败';
        message = '对方先完成了扫雷，你失败了';
        }
    }

    return (
        <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
        }}>
        <div style={{
            background: 'rgba(30,30,40,0.95)',
            borderRadius: '20px',
            padding: '30px',
            textAlign: 'center',
            maxWidth: '400px',
            width: '80%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.2)'
        }}>
            <h2 style={{ margin: '0 0 20px', color: isWinner ? '#f39c12' : '#e74c3c' }}>
            {title}
            </h2>
            <p style={{ color: '#ddd', marginBottom: '20px' }}>
            {message}
            </p>
            <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '20px' }}>
            💡 所有地雷已自动显示，红色格子为雷区。
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
                onClick={handleCloseOverlay}
                style={{
                background: '#2c3e50',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '30px',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer'
                }}
            >
                继续查看棋盘
            </button>
            <button
                onClick={handleBackToCenter}
                style={{
                background: '#f39c12',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '30px',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer'
                }}
            >
                返回游戏中心
            </button>
            </div>
        </div>
        </div>
    );
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px', background: '#1a252f', minHeight: '100vh', position: 'relative' }}>
      {renderGameOverlay()}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <button onClick={handleBackToCenter} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer' }}>← 退出游戏</button>
        <div style={{ display: 'flex', gap: '20px', background: '#2c3e50', padding: '8px 16px', borderRadius: '30px', color: 'white' }}>
          <span>💣 剩余雷数: {totalMines - flaggedCount}</span>
          <span>🚩 旗子: {flaggedCount}</span>
          <span>📊 进度: {progress}%</span>
        </div>
        <div style={{ color: 'white', fontSize: '18px' }}>{gameEnded ? (winner === userId ? '胜利' : '失败') : '⚔️ 对战中'}</div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '40px' }}>
        <div style={{ background: '#34495e', padding: '10px', borderRadius: '10px' }}>
          <h3 style={{ color: 'white', marginTop: 0 }}>你的雷区</h3>
          <div style={{ display: 'inline-block', background: '#2c3e50', padding: '5px', borderRadius: '8px' }}>
            {board.map((row, r) => (
              <div key={r} style={{ display: 'flex' }}>
                {row.map((cell, c) => renderCell(cell, myRevealed[r]?.[c], r, c, true, myFlags[r]?.[c]))}
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: '#34495e', padding: '10px', borderRadius: '10px' }}>
          <h3 style={{ color: 'white', marginTop: 0 }}>对手雷区</h3>
          <div style={{ display: 'inline-block', background: '#2c3e50', padding: '5px', borderRadius: '8px' }}>
            {board.map((row, r) => (
              <div key={r} style={{ display: 'flex' }}>
                {row.map((cell, c) => renderCell(cell, opponentRevealed[r]?.[c], r, c, false, false))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MinesweeperGame;